import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useMount } from 'react-use';

import { useAppState } from '@upheal/shared/state';
import { appServer } from '@upheal/shared/utils/api';
import { browserStorage } from '@upheal/shared/utils/cache/browserStorage';
import { OTP_CLIENT_SIGN_IN_PATH } from '@upheal/shared/utils/configuration';
import { captureException } from '@upheal/shared/utils/sentry';
import { AuthResponse } from '@upheal/shared/types';
import {
    hasOnlineCall,
    isWhiteLabelApp,
} from '@upheal/design-system/theme/legacy/is-whitelabel';
import logger from '@upheal/utils/logger';
import { redactText } from '@upheal/utils/security';
import { clearLastActiveTime } from '@upheal/shared/utils/useTerminateInactiveUserSession.utils';

type WhiteLabelRole = 'provider' | 'client';

type AuthenticatedState = {
    status: 'authenticated';
    role: WhiteLabelRole;
    ownerUserId?: string;
};

type AuthFailedState = { status: 'auth_failed' };

type AuthResult = AuthenticatedState | AuthFailedState;

type WhiteLabelOtpAuthState =
    | { status: 'not_applicable' }
    | { status: 'otp_missing' }
    | { status: 'authenticating'; promise: Promise<AuthResult> }
    | AuthenticatedState
    | AuthFailedState;

const getRoleFromLegacyRoutes = (): WhiteLabelRole | null => {
    if (window.location.pathname.startsWith('/client-video')) {
        return 'client';
    }
    if (window.location.pathname.startsWith('/video')) {
        return 'provider';
    }

    return null;
};

export const getOtpParams = (): {
    sessionToken: string | null;
    role: WhiteLabelRole | null;
} => {
    const params = new URLSearchParams(window.location.search);
    const role = params.get('role') ?? getRoleFromLegacyRoutes();

    return {
        sessionToken: params.get('sessionToken'),
        role: role === 'provider' || role === 'client' ? role : null,
    };
};

export const initialSessionToken: string | undefined =
    getOtpParams().sessionToken ?? undefined;

const removeOtpParams = (history: ReturnType<typeof useHistory>) => {
    const params = new URLSearchParams(history.location.search);
    params.delete('sessionToken');
    params.delete('role');

    history.replace({
        search: params.toString(),
    });
};

const isOtpExpected = isWhiteLabelApp && hasOnlineCall;

const getInitialState = ({
    hasOtpParams,
    authPromise,
}: {
    hasOtpParams: boolean;
    authPromise: Promise<AuthResult>;
}): WhiteLabelOtpAuthState => {
    if (hasOtpParams) {
        logger.debug('[white-label-otp] OTP params detected, starting auth');
        return { status: 'authenticating', promise: authPromise };
    }

    if (isOtpExpected) {
        logger.warn('[white-label-otp] OTP expected but missing in URL');
        return { status: 'otp_missing' };
    }

    return { status: 'not_applicable' };
};

/**
 * Handles OTP authentication for white-label apps accessing telehealth via uphealer links.
 *
 * White-label apps use URLs like:
 * - Provider: /u/:roomName?contractId=X&sessionToken=OTP&role=provider
 * - Client: /u/:roomName?sessionToken=OTP&role=client
 *
 * This hook detects the OTP params, authenticates the user, and removes the params from URL.
 *
 * States:
 * - 'authenticating': OTP params found, authentication in progress
 * - 'authenticated': OTP auth succeeded
 * - 'auth_failed': OTP auth request failed
 * - 'otp_missing': White-label app expects OTP but none provided in URL
 * - 'not_applicable': Not a white-label app, standard flow proceeds
 */
export const useWhiteLabelOtpAuth = (): WhiteLabelOtpAuthState => {
    const history = useHistory();
    const { signInWithOtp, getMe } = useAppState();

    const { sessionToken, role } = getOtpParams();
    const hasOtpParams = Boolean(sessionToken);

    const [authPromise] = useState(() => {
        let resolve: (result: AuthResult) => void;
        const promise = new Promise<AuthResult>((r) => {
            resolve = r;
        });
        return { promise, resolve: resolve! };
    });

    const [state, setState] = useState<WhiteLabelOtpAuthState>(
        getInitialState({
            hasOtpParams,
            authPromise: authPromise.promise,
        }),
    );

    useMount(async () => {
        if (!sessionToken) {
            return;
        }

        const effectiveRole = role ?? 'provider';

        logger.debug('[white-label-otp] Authenticating', {
            role: effectiveRole,
        });

        try {
            let ownerUserId: string | undefined;
            if (effectiveRole === 'provider') {
                logger.debug(
                    '[provider-otp-auth] Clearing activity user timestamp',
                    {
                        oneTimePassword: redactText(sessionToken, 6, 3),
                    },
                );
                clearLastActiveTime();

                await signInWithOtp(sessionToken);
            } else if (effectiveRole === 'client') {
                const { data } = await appServer.request({
                    method: 'POST',
                    url: OTP_CLIENT_SIGN_IN_PATH,
                    data: { otp: sessionToken },
                });

                if (
                    !data?.accessToken ||
                    !data?.expiresInSeconds ||
                    !data?.accessTokenExpiresInSeconds ||
                    !data?.refreshTokenExpiresInSeconds ||
                    !data?.ownerUserId
                ) {
                    throw new Error('Invalid auth response from server');
                }

                const authResponse: AuthResponse = {
                    accessToken: data.accessToken,
                    expiresInSeconds: data.expiresInSeconds,
                    accessTokenExpiresInSeconds:
                        data.accessTokenExpiresInSeconds,
                    refreshTokenExpiresInSeconds:
                        data.refreshTokenExpiresInSeconds,
                };

                ownerUserId = data.ownerUserId;

                browserStorage.saveAuthToken({
                    ...authResponse,
                    isClient: true,
                });

                await getMe();
            }

            removeOtpParams(history);
            logger.info('[white-label-otp] Authentication successful', {
                role: effectiveRole,
            });

            const result: AuthenticatedState = {
                status: 'authenticated',
                role: effectiveRole,
                ownerUserId,
            };
            setState(result);
            authPromise.resolve(result);
        } catch (error) {
            captureException(
                '[white-label-otp] Failed to authenticate with OTP',
                { error, role: effectiveRole },
            );
            removeOtpParams(history);

            const result: AuthFailedState = { status: 'auth_failed' };
            setState(result);
            authPromise.resolve(result);
        }
    });

    return state;
};
